import React, { useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Col, Container, Row, Tab, Tabs } from "react-bootstrap";
import { useNavigate, useParams } from "react-router";
import Moment from "react-moment";
import Loading from "../Common/Loading";

/**
 * Component representing the details of a Waylens Event, including metadata, event information,
 * and visual representations of event-specific data.
 *
 * This component fetches data from the API based on the event ID, displays a detailed summary,
 * organizes data into categorized tables, and manages tab-based navigation for different content views.
 *
 * @param {Object} props - The component properties.
 * @param {Function} props.apiFetch - Function for making API requests.
 */
export const WaylensEventDetail = ({ apiFetch }) => {
    document.title = "AGMT | Waylens Event";

    const { id } = useParams();
    const navigate = useNavigate();

    const [event, setEvent] = useState(null);
    const [fetching, setFetching] = useState(false);
    const [error, setError] = useState("");
    const [activeTab, setActiveTab] = useState("summary");

    const clipUrl = event?.clipUrl;
    const thumbnailUrl = event?.thumbnailUrl || null;

    const HEADER_HEIGHT_PX = 72;
    const GAP_PX = 12;
    const PANEL_HEIGHT = `calc(100vh - ${HEADER_HEIGHT_PX + GAP_PX * 2}px)`;

    const pageStyles = {
        minHeight: "100vh",
        paddingBottom: "0.5rem",
    };

    const cardStyles = {
        backgroundColor: "#0b0f14",
        borderColor: "#1c2530",
        boxShadow: "0 0.25rem 0.75rem rgba(0,0,0,0.35)",
    };

    const mutedText = { color: "#9aa4ad" };

    const tabsContainerStyles = {
        backgroundColor: "#0b0f14",
        border: "1px solid #1c2530",
        borderRadius: "0.5rem",
        padding: "0.25rem",
    };

    const tabsCss = `
    .waylens-tabs.nav-tabs { border-bottom: none !important; }
    .waylens-tabs.nav-tabs .nav-link {
      color: #c9d1d9 !important;
      border: 1px solid #1c2530 !important;
      background: #0e141c !important;
      margin: 0 0.25rem !important;
      border-radius: 0.5rem !important;
      padding: 0.5rem 0.75rem !important;
    }
    .waylens-tabs.nav-tabs .nav-link:hover {
      color: #e6edf3 !important;
      border-color: #2b3a4a !important;
      background: #111a24 !important;
    }
    .waylens-tabs.nav-tabs .nav-link.active {
      color: #0b0f14 !important;
      border-color: #7cc4ff !important;
      background: #7cc4ff !important;
      font-weight: 600 !important;
    }
    .waylens-tabs.nav-tabs .nav-link:focus {
      box-shadow: 0 0 0 0.2rem rgba(124,196,255,0.2) !important;
    }
    .waylens-summary-layout {
      display: grid;
      grid-template-columns: minmax(0, 1.65fr) minmax(340px, 0.85fr);
      gap: 1rem;
      min-height: 100%;
      align-items: stretch;
    }
    .waylens-summary-video {
      min-height: 460px;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }
    .waylens-summary-media {
      flex: 1;
      min-height: 0;
    }
    .waylens-summary-details {
      min-width: 0;
      border-left: 1px solid #1c2530;
      padding-left: 1rem;
    }
    .waylens-summary-section {
      padding: 0 0 1rem;
      margin-bottom: 1rem;
      border-bottom: 1px solid #1c2530;
    }
    .waylens-summary-section:last-child {
      padding-bottom: 0;
      margin-bottom: 0;
      border-bottom: 0;
    }
    .waylens-summary-section-heading {
      color: #e6edf3;
      font-size: 0.95rem;
      font-weight: 700;
      margin-bottom: 0.75rem;
    }
    .waylens-summary-time-grid {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 1rem;
    }
    .waylens-summary-time {
      min-width: 0;
      padding-left: 0.75rem;
      border-left: 2px solid #2b3a4a;
    }
    .waylens-summary-label {
      color: #9aa4ad;
      font-size: 0.88rem;
      line-height: 1.3;
    }
    .waylens-summary-value {
      min-width: 0;
      color: #e6edf3;
      font-size: 0.98rem;
      font-weight: 600;
      line-height: 1.35;
      overflow-wrap: anywhere;
    }
    .waylens-summary-subvalue {
      color: #9aa4ad;
      font-size: 0.88rem;
      font-weight: 400;
      margin-top: 0.15rem;
    }
    .waylens-summary-facts {
      display: grid;
      grid-template-columns: minmax(130px, 0.75fr) minmax(0, 1.25fr);
      gap: 0.6rem 1rem;
      align-items: baseline;
    }
    .waylens-data-stack {
      display: flex;
      flex-direction: column;
      gap: 1.25rem;
    }
    .waylens-data-heading {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      color: #e6edf3;
      font-size: 1rem;
      font-weight: 700;
      margin-bottom: 0.65rem;
    }
    .waylens-data-heading::after {
      content: "";
      height: 1px;
      flex: 1;
      background: #1c2530;
    }
    .waylens-data-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
      border-top: 1px solid #1c2530;
      border-left: 1px solid #1c2530;
    }
    .waylens-data-item {
      display: grid;
      grid-template-columns: minmax(130px, 0.8fr) minmax(0, 1.2fr);
      gap: 1rem;
      align-items: start;
      min-height: 68px;
      padding: 0.75rem;
      border-right: 1px solid #1c2530;
      border-bottom: 1px solid #1c2530;
      background: #0e141c;
    }
    .waylens-data-item:nth-child(even) {
      background: #0c1219;
    }
    .waylens-data-label {
      color: #9aa4ad;
      font-size: 0.9rem;
      line-height: 1.35;
    }
    .waylens-data-value {
      min-width: 0;
      color: #e6edf3;
      font-size: 0.96rem;
      font-weight: 600;
      line-height: 1.4;
      overflow-wrap: anywhere;
    }
    .waylens-data-subvalue {
      display: block;
      color: #9aa4ad;
      font-size: 0.88rem;
      font-weight: 400;
      margin-top: 0.15rem;
    }
    @media (max-width: 991.98px) {
      .waylens-summary-layout {
        grid-template-columns: minmax(0, 1fr);
      }
      .waylens-summary-video {
        min-height: 0;
      }
      .waylens-summary-media {
        min-height: clamp(240px, 56vw, 480px);
      }
      .waylens-summary-details {
        border-left: 0;
        border-top: 1px solid #1c2530;
        padding-top: 1rem;
        padding-left: 0;
      }
    }
    @media (max-width: 575.98px) {
      .waylens-summary-time-grid {
        grid-template-columns: minmax(0, 1fr);
      }
      .waylens-summary-facts {
        grid-template-columns: minmax(110px, 0.8fr) minmax(0, 1.2fr);
        gap: 0.6rem 0.75rem;
      }
      .waylens-data-grid {
        grid-template-columns: minmax(0, 1fr);
      }
      .waylens-data-item {
        grid-template-columns: minmax(110px, 0.8fr) minmax(0, 1.2fr);
        gap: 0.75rem;
      }
    }
  `;

    useEffect(() => {
        const load = async () => {
            setFetching(true);
            setError("");

            try {
                const response = await apiFetch(`/api/Waylens/event/${id}`);
                if (!response.ok) {
                    setError(`Failed to load event. Status ${response.status}`);
                    setEvent(null);
                    return;
                }

                const payload = await response.json();
                setEvent(payload);
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error(e);
                setError("Failed to load event due to a network or server error.");
                setEvent(null);
            } finally {
                setFetching(false);
            }
        };

        if (id) load();
    }, [apiFetch, id]);

    const pairingStatus = event?.isIcxCameraPairMatch === true
        ? { label: "Match", bg: "success" }
        : event?.isIcxCameraPairMatch === false
            ? { label: "Mismatch", bg: "danger" }
            : { label: "Unknown", bg: "secondary" };

    const renderValue = (k, v) => {
        if (v === null || v === undefined) return "-";

        const lower = k.toLowerCase();
        if (lower.includes("utc") || lower.includes("datetime") || lower.includes("timestamp")) {
            return (
                <div>
                    <div><Moment fromNow>{v}</Moment></div>
                    <small className="waylens-data-subvalue">
                        <Moment format="YYYY-MM-DD HH:mm:ss">{v}</Moment>
                    </small>
                </div>
            );
        }

        if (typeof v === "boolean") return v ? "true" : "false";
        return String(v);
    };

    const formatLabel = (key) => {
        if (key === "icxSecondsSinceEventDetected") return "ICX Delay Since Detection (s)";

        const withSpaces = key
            .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
            .replace(/^icx/i, "ICX")
            .replace(/\bUtc\b/g, "UTC")
            .replace(/\bId\b/g, "ID");
        return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
    };

    const summary = useMemo(() => {
        if (!event) return null;
        return {
            eventName: event.event ?? "Waylens Event",
            device: event.deviceSerialNumber ?? "-",
            receivedUtc: event.receivedUtc ?? null,
            eventTimeUtc: event.eventTimeUtc ?? null,
            icxEventType: event.icxEventType ?? null,
            icxMacAddress: event.icxMacAddress ?? null,
            icxEventTypeCount: event.icxEventTypeCount ?? null,
            icxSecondsSinceEventDetected: event.icxSecondsSinceEventDetected ?? null,
            isIcxCameraPairMatch: event.isIcxCameraPairMatch ?? null,
            expectedIcxMacAddresses: event.expectedIcxMacAddresses ?? [],
            cameraRegisteredToCurrentIcx: event.cameraRegisteredToCurrentIcx ?? null,
        };
    }, [event]);

    const tables = useMemo(() => {
        if (!event) return { eventRows: [], icxGroups: [] };

        const excluded = new Set([
            "clipUrl",
            "thumbnailUrl",
            "isIcxCameraPairMatch",
            "expectedIcxMacAddresses",
            "cameraRegisteredToCurrentIcx",
        ]);
        const keys = Object.keys(event).filter((k) => !excluded.has(k));

        const icxKeys = keys.filter((k) => k.toLowerCase().startsWith("icx"));
        const nonIcxKeys = keys.filter((k) => !k.toLowerCase().startsWith("icx"));

        const nonIcxPreferred = [
            "id",
            "deviceSerialNumber",
            "notificationType",
            "tripId",
            "event",
            "eventCategory",
            "eventLevel",
            "eventTimeUtc",
            "receivedUtc",
            "clipId",
            "concurrentEventType",
        ];

        const nonIcxRest = nonIcxKeys.filter((k) => !nonIcxPreferred.includes(k)).sort();
        const eventOrder = [...nonIcxPreferred.filter((k) => nonIcxKeys.includes(k)), ...nonIcxRest];

        const eventRows = eventOrder.map((k) => ({
            key: k,
            label: formatLabel(k),
            value: event[k],
        }));

        const groupDefs = [
            {
                title: "Identity & Debug",
                keys: [
                    "icxRawHex",
                    "icxMacAddress",
                    "icxEventType",
                    "icxEventTypeCount",
                    "icxSecondsSinceEventDetected",
                    "icxDebugPayloadHex",
                ],
            },
            {
                title: "Raw Readings",
                keys: [
                    "icxMic1Raw",
                    "icxMic1RefRaw",
                    "icxMic2Raw",
                    "icxMic2RefRaw",
                    "icxMic3Raw",
                    "icxMic3RefRaw",
                    "icxPiezoRaw",
                    "icxPiezoRefRaw",
                ],
            },
            {
                title: "Voltages",
                keys: [
                    "icxMic1Volts",
                    "icxMic1RefVolts",
                    "icxMic2Volts",
                    "icxMic2RefVolts",
                    "icxMic3Volts",
                    "icxMic3RefVolts",
                    "icxPiezoVolts",
                    "icxPiezoRefVolts",
                ],
            },
            {
                title: "Delta Voltages",
                keys: [
                    "icxMic1DeltaVolts",
                    "icxMic2DeltaVolts",
                    "icxMic3DeltaVolts",
                    "icxPiezoDeltaVolts",
                ],
            },
        ];

        const present = new Set(icxKeys);

        const icxGroups = groupDefs
            .map((g) => {
                const rows = g.keys
                    .filter((k) => present.has(k))
                    .map((k) => ({
                        key: k,
                        label: formatLabel(k),
                        value: event[k],
                    }));
                return { title: g.title, rows };
            })
            .filter((g) => g.rows.length > 0);

        const groupedKeys = new Set(icxGroups.flatMap((g) => g.rows.map((r) => r.key)));
        const icxOtherKeys = icxKeys.filter((k) => !groupedKeys.has(k)).sort();
        if (icxOtherKeys.length > 0) {
            icxGroups.push({
                title: "Other ICX",
                rows: icxOtherKeys.map((k) => ({
                    key: k,
                    label: formatLabel(k),
                    value: event[k],
                })),
            });
        }

        return { eventRows, icxGroups };
    }, [event]);

    const renderKeyValueGrid = (rows) => (
        <div className="waylens-data-grid">
            {rows.map((row) => (
                <div className="waylens-data-item" key={row.key}>
                    <div className="waylens-data-label">{row.label}</div>
                    <div className="waylens-data-value">{renderValue(row.key, row.value)}</div>
                </div>
            ))}
        </div>
    );

    const renderIcxGroups = () => {
        if (tables.icxGroups.length === 0) {
            return <div style={mutedText}>No ICX data available for this event.</div>;
        }

        return (
            <div className="waylens-data-stack">
                {tables.icxGroups.map((group) => (
                    <section key={group.title}>
                        <div className="waylens-data-heading">{group.title}</div>
                        {renderKeyValueGrid(group.rows)}
                    </section>
                ))}
            </div>
        );
    };

    return (
        <div className="webcontainer" style={pageStyles}>
            <style>{tabsCss}</style>

            <Container fluid>
                <Row className="mb-2">
                    <Col md={12} lg={12} className="fixed-col2">
                        <div
                            className="contentvehicle3"
                            style={{
                                backgroundColor: "#0b0f14",
                                border: "1px solid #1c2530",
                                borderRadius: "0.5rem",
                                padding: "0.75rem 1rem",
                                display: "grid",
                                gridTemplateColumns: "auto 1fr auto",
                                alignItems: "center",
                                columnGap: "0.75rem",
                            }}
                        >
                            <div style={{ justifySelf: "start" }}>
                                <Button
                                    variant="secondary"
                                    onClick={() => {
                                        if (window.history.length > 1) {
                                            navigate(-1);
                                        } else {
                                            navigate("/waylens");
                                        }
                                    }}
                                    style={{ width: "auto", minWidth: "90px" }}
                                >
                                    Back
                                </Button>
                            </div>

                            <div
                                style={{
                                    justifySelf: "center",
                                    color: "#e6edf3",
                                    fontSize: "1.25rem",
                                    fontWeight: 600,
                                    textAlign: "center",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                }}
                            >
                                Waylens Event Detail
                            </div>

                            <div style={{ width: "90px" }} />
                        </div>
                    </Col>
                </Row>

                {fetching && (
                    <Row>
                        <Col md={12} lg={12}>
                            <Loading />
                        </Col>
                    </Row>
                )}

                {!fetching && error !== "" && (
                    <Row>
                        <Col md={12} lg={12}>
                            <div className="alert alert-danger">{error}</div>
                        </Col>
                    </Row>
                )}

                {!fetching && !error && event && (
                    <Row className="g-3" style={{ alignItems: "stretch" }}>
                        <Col xs={12} style={{ display: "flex" }}>
                            <Card bg="dark" text="light" style={{ ...cardStyles, width: "100%", height: PANEL_HEIGHT }}>
                                <Card.Body
                                    style={{
                                        backgroundColor: "#0b0f14",
                                        padding: "0.75rem",
                                        minHeight: 0,
                                    }}
                                >
                                    <div style={tabsContainerStyles}>
                                        <Tabs
                                            activeKey={activeTab}
                                            onSelect={(k) => setActiveTab(k)}
                                            className="waylens-tabs"
                                            justify
                                        >
                                            <Tab eventKey="summary" title="Summary" />
                                            <Tab eventKey="event" title="Event" />
                                            <Tab eventKey="icx" title="ICX" />
                                        </Tabs>
                                    </div>

                                    <div
                                        style={{
                                            height: "calc(100% - 64px)",
                                            overflowY: "auto",
                                            paddingRight: "0.25rem",
                                            marginTop: "0.75rem",
                                        }}
                                    >
                                        {activeTab === "summary" && (
                                            <div className="waylens-summary-layout">
                                                <div className="waylens-summary-video">
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            justifyContent: "space-between",
                                                            alignItems: "center",
                                                            gap: "0.75rem",
                                                            minHeight: "1.5rem",
                                                        }}
                                                    >
                                                        <span style={{ color: "#e6edf3", fontWeight: 600 }}>Video</span>
                                                        {clipUrl ? (
                                                            <a
                                                                href={clipUrl}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                                style={{ color: "#7cc4ff" }}
                                                            >
                                                                Open clip
                                                            </a>
                                                        ) : null}
                                                    </div>

                                                    {clipUrl ? (
                                                        <video
                                                            className="waylens-summary-media"
                                                            controls
                                                            preload="metadata"
                                                            poster={thumbnailUrl || undefined}
                                                            style={{
                                                                width: "100%",
                                                                height: "100%",
                                                                borderRadius: "0.5rem",
                                                                backgroundColor: "#05070a",
                                                                border: "1px solid #1c2530",
                                                                objectFit: "contain",
                                                            }}
                                                            src={clipUrl}
                                                        />
                                                    ) : (
                                                        <div
                                                            className="waylens-summary-media d-flex align-items-center justify-content-center"
                                                            style={{
                                                                borderRadius: "0.5rem",
                                                                backgroundColor: "#05070a",
                                                                border: "1px solid #1c2530",
                                                                color: "#9aa4ad",
                                                                textAlign: "center",
                                                                padding: "1rem",
                                                            }}
                                                        >
                                                            <div>
                                                                <div
                                                                    style={{
                                                                        fontSize: "1.1rem",
                                                                        marginBottom: "0.25rem",
                                                                        color: "#e6edf3",
                                                                    }}
                                                                >
                                                                    No video clip available
                                                                </div>
                                                                <div style={{ fontSize: "0.95rem" }}>
                                                                    This event does not have an archived video clip.
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="waylens-summary-details">
                                                    <section className="waylens-summary-section">
                                                        <div className="waylens-summary-section-heading">Timing</div>
                                                        <div className="waylens-summary-time-grid">
                                                            <div className="waylens-summary-time">
                                                                <div className="waylens-summary-label">Received</div>
                                                                <div className="waylens-summary-value">
                                                                    {summary?.receivedUtc ? (
                                                                        <>
                                                                            <Moment fromNow>{summary.receivedUtc}</Moment>
                                                                            <div className="waylens-summary-subvalue">
                                                                                <Moment format="YYYY-MM-DD HH:mm:ss">
                                                                                    {summary.receivedUtc}
                                                                                </Moment>
                                                                            </div>
                                                                        </>
                                                                    ) : (
                                                                        "-"
                                                                    )}
                                                                </div>
                                                            </div>

                                                            <div className="waylens-summary-time">
                                                                <div className="waylens-summary-label">Event Time</div>
                                                                <div className="waylens-summary-value">
                                                                    {summary?.eventTimeUtc ? (
                                                                        <>
                                                                            <Moment fromNow>{summary.eventTimeUtc}</Moment>
                                                                            <div className="waylens-summary-subvalue">
                                                                                <Moment format="YYYY-MM-DD HH:mm:ss">
                                                                                    {summary.eventTimeUtc}
                                                                                </Moment>
                                                                            </div>
                                                                        </>
                                                                    ) : (
                                                                        "-"
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </section>

                                                    <section className="waylens-summary-section">
                                                        <div className="waylens-summary-section-heading">Event Details</div>
                                                        <div className="waylens-summary-facts">
                                                            <div className="waylens-summary-label">Camera SN</div>
                                                            <div className="waylens-summary-value">{summary?.device ?? "-"}</div>

                                                            <div className="waylens-summary-label">Event Type</div>
                                                            <div className="waylens-summary-value">
                                                                {summary?.eventName ?? "-"}
                                                                {summary?.icxEventType !== null
                                                                && summary?.icxEventType !== undefined
                                                                    ? ` (${summary.icxEventType})`
                                                                    : ""}
                                                            </div>

                                                            <div className="waylens-summary-label">ICX MAC Address</div>
                                                            <div className="waylens-summary-value">
                                                                {summary?.icxMacAddress ?? "-"}
                                                            </div>

                                                            <div className="waylens-summary-label">Event Type Count</div>
                                                            <div className="waylens-summary-value">
                                                                {summary?.icxEventTypeCount ?? "-"}
                                                            </div>

                                                            <div className="waylens-summary-label">ICX Delay Since Detection</div>
                                                            <div className="waylens-summary-value">
                                                                {summary?.icxSecondsSinceEventDetected !== null
                                                                && summary?.icxSecondsSinceEventDetected !== undefined
                                                                    ? `${summary.icxSecondsSinceEventDetected} s`
                                                                    : "-"}
                                                            </div>
                                                        </div>
                                                    </section>

                                                    <section className="waylens-summary-section">
                                                        <div
                                                            className="waylens-summary-section-heading"
                                                            style={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "space-between",
                                                                gap: "0.75rem",
                                                            }}
                                                        >
                                                            <span>Camera–ICX Pairing</span>
                                                            <Badge bg={pairingStatus.bg}>{pairingStatus.label}</Badge>
                                                        </div>

                                                        <div className="waylens-summary-facts">
                                                            <div className="waylens-summary-label">Current ICX</div>
                                                            <div
                                                                className="waylens-summary-value"
                                                                style={{
                                                                    color: summary?.isIcxCameraPairMatch === false
                                                                        ? "#ff7b72"
                                                                        : "#e6edf3",
                                                                }}
                                                            >
                                                                {summary?.icxMacAddress ?? "-"}
                                                            </div>

                                                            <div className="waylens-summary-label">Expected ICX(s)</div>
                                                            <div className="waylens-summary-value" style={{ color: "#7ee787" }}>
                                                                {summary?.expectedIcxMacAddresses.length > 0
                                                                    ? summary.expectedIcxMacAddresses.join(", ")
                                                                    : "-"}
                                                            </div>

                                                            <div className="waylens-summary-label">Camera for Current ICX</div>
                                                            <div className="waylens-summary-value">
                                                                {summary?.cameraRegisteredToCurrentIcx ?? "-"}
                                                            </div>
                                                        </div>
                                                    </section>
                                                </div>
                                            </div>
                                        )}

                                        {activeTab === "event" && (
                                            <section>
                                                <div className="waylens-data-heading">Event Data</div>
                                                {renderKeyValueGrid(tables.eventRows)}
                                            </section>
                                        )}

                                        {activeTab === "icx" && renderIcxGroups()}
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                )}
            </Container>
        </div>
    );
};
